<script setup>
/**
 * Troca a corporação ativa sem sair da plataforma.
 *
 * Até aqui a corporação era escolhida no login e não mudava mais. O `setCorp`
 * já existia no serviço -- e já atualiza o `authStore` -- mas nenhuma tela o
 * chamava. Quem precisava operar em outra empresa encerrava a sessão e entrava
 * de novo.
 *
 * Irmão do componente de mesmo nome no `apps/accounts`: mudança em um costuma
 * valer para o outro.
 *
 * A lista sai do `listCorps`, que lê as corporações do próprio usuário do
 * `localStorage.credentials` -- não é chamada de API, e por isso o botão abre
 * instantâneo.
 *
 * Só aparece com mais de uma corporação: para quem tem uma só, um seletor de
 * um item é ruído.
 */
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { listCorps, setCorp } from "@/services/api/corporation.js";
import { handleError } from "@/utils/errors";

const props = defineProps({
    corpAtual: { type: Object, default: null },
});

const { t } = useI18n();
const corporacoes = ref([]);
const trocando = ref(null);

const podeTrocar = computed(() => corporacoes.value.length > 1);

const nomeAtual = computed(
    () => props.corpAtual?.companyName || props.corpAtual?.company_name || ""
);

async function trocar(corp) {
    if (trocando.value || corp.id === props.corpAtual?.id) return;

    try {
        trocando.value = corp.id;
        await setCorp({ id: corp.id });

        // Recarga inteira, e não só um refresh de estado: a corporação decide
        // permissão, menu e os dados de toda tela aberta. Repintar por partes
        // deixaria pedaços da empresa anterior na tela, que é pior do que
        // esperar dois segundos.
        window.location.reload();
    } catch (erro) {
        trocando.value = null;
        message.error(await handleError(erro, t));
    }
}

onMounted(async () => {
    try {
        corporacoes.value = await listCorps({});
    } catch {
        // Sem a lista o botão simplesmente não aparece, e o cabeçalho segue
        // mostrando a corporação atual.
        corporacoes.value = [];
    }
});
</script>

<template>
    <v-menu v-if="podeTrocar" location="bottom start">
        <template #activator="{ props: ativador }">
            <button
                v-bind="ativador"
                class="trocar-corp"
                :title="t('common.topHeader.trocarCorporacao')"
                type="button"
            >
                <span class="material-symbols-outlined">unfold_more</span>
            </button>
        </template>

        <v-list density="compact" class="lista-corp">
            <v-list-subheader>{{ t('common.topHeader.trocarCorporacao') }}</v-list-subheader>
            <v-list-item
                v-for="corp in corporacoes"
                :key="corp.id"
                :active="corp.id === corpAtual?.id"
                :disabled="trocando !== null"
                @click="trocar(corp)"
            >
                <template #prepend>
                    <span
                        class="material-symbols-outlined marca"
                        :class="{ invisivel: corp.id !== corpAtual?.id }"
                    >check</span>
                </template>
                <v-list-item-title>{{ corp.companyName }}</v-list-item-title>
                <template #append>
                    <v-progress-circular
                        v-if="trocando === corp.id"
                        indeterminate
                        size="16"
                        width="2"
                    />
                </template>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<style lang="scss" scoped>
.trocar-corp {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    padding: 2px;
    border: none;
    background: transparent;
    color: var(--secondaryColor, #6b7280);
    cursor: pointer;
    border-radius: 4px;
    line-height: 1;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .material-symbols-outlined {
        font-size: 20px;
    }
}

.lista-corp {
    min-width: 240px;

    .marca {
        font-size: 18px;
        margin-right: 6px;
    }

    // Mantém o alinhamento: esconder com v-if faria o texto dançar.
    .invisivel {
        visibility: hidden;
    }
}
</style>
